import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Tab, TabList, TabGroup, TabPanel, TabPanels, Dialog, DialogPanel } from '@tremor/react';
import { ArrowLeft, Save, Play, Code, AlertCircle } from 'lucide-react';

export function AgentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    channel: 'WEB',
    status: 'DRAFT',
    greeting: '',
    fallbackMessage: '',
    policySetId: '',
    allowedSkills: [],
    icon: ''
  });
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [deploymentConfig, setDeploymentConfig] = useState({
    webConfig: {
      allowAttachments: false,
      maxAttachmentSize: 5,
      enableAudioMessages: false,
      position: 'bottom-right',
      theme: 'light'
    },
    kioskConfig: {
      fullScreen: true,
      idleTimeout: 300,
      requireAuthentication: false,
      printerEnabled: true
    },
    voiceConfig: {
      language: 'en-US',
      voice: 'neural-1',
      speed: 1.0,
      pitch: 1.0
    }
  });

  // Import mockFetch from mock-api.js at the top of the file
  const { isLoading: loading, error, data } = useQuery({
    queryKey: ['agent', id],
    queryFn: async () => {
      // Use the actual import in the real component
      const mockFetch = (await import('../../../lib/mock-api')).mockFetch;
      const response = await mockFetch(`/api/agents/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch agent');
      }
      return response.json();
    },
    enabled: id !== 'new'
  });

  const { data: policiesData } = useQuery({
    queryKey: ['policies'],
    queryFn: async () => {
      const mockFetch = (await import('../../../lib/mock-api')).mockFetch;
      const response = await mockFetch('/api/policies');
      if (!response.ok) {
        throw new Error('Failed to fetch policies');
      }
      return response.json();
    }
  });

  const { data: skillsData } = useQuery({
    queryKey: ['skills'],
    queryFn: async () => {
      const mockFetch = (await import('../../../lib/mock-api')).mockFetch;
      const response = await mockFetch('/api/skills');
      if (!response.ok) {
        throw new Error('Failed to fetch skills');
      }
      return response.json();
    }
  });

  const updateAgentMutation = useMutation({
    mutationFn: async (variables) => {
      const mockFetch = (await import('../../../lib/mock-api')).mockFetch;
      const response = await mockFetch(`/api/agents/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables)
      });
      if (!response.ok) {
        throw new Error('Failed to update agent');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['agent', id]);
      navigate(-1);
    }
  });

  const createAgentMutation = useMutation({
    mutationFn: async (variables) => {
      const mockFetch = (await import('../../../lib/mock-api')).mockFetch;
      const response = await mockFetch('/api/agents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables)
      });
      if (!response.ok) {
        throw new Error('Failed to create agent');
      }
      return response.json();
    },
    onSuccess: (data) => {
      navigate(`/ai/agents/${data.id}`);
    }
  });

  const testAgentMutation = useMutation({
    mutationFn: async ({ id, message }) => {
      const mockFetch = (await import('../../../lib/mock-api')).mockFetch;
      const response = await mockFetch(`/api/agents/${id}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message })
      });
      if (!response.ok) {
        throw new Error('Failed to test agent');
      }
      return response.json();
    },
    onSuccess: (response) => {
      setTestHistory(prev => [
        ...prev,
        { role: 'user', content: testMessage, timestamp: new Date() },
        response
      ]);
      setTestResponse(response);
      setTestMessage('');
    }
  });

  useEffect(() => {
    if (data?.agent) {
      setFormData(data.agent);
      if (data.agent.deploymentConfig) {
        try {
          setDeploymentConfig(JSON.parse(data.agent.deploymentConfig));
        } catch (err) {
          console.error('Error parsing deployment config:', err);
        }
      }
    }
  }, [data]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleDeploymentConfigChange = (section, key, value) => {
    setDeploymentConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleNavigateAway = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      navigate(-1);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.greeting.trim()) {
      errors.greeting = 'Greeting message is required';
    }
    if (!formData.fallbackMessage.trim()) {
      errors.fallbackMessage = 'Fallback message is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const input = {
        ...formData,
        deploymentConfig: JSON.stringify(deploymentConfig)
      };

      if (id === 'new') {
        await createAgentMutation.mutateAsync(input);
      } else {
        await updateAgentMutation.mutateAsync(input);
      }
    } catch (err) {
      console.error('Error saving agent:', err);
      setFormErrors(prev => ({
        ...prev,
        submit: err.message
      }));
    }
  };

  const handleTestAgent = async () => {
    if (!testMessage.trim()) {
      setTestError('Please enter a message to test');
      return;
    }

    setTestLoading(true);
    setTestError(null);

    try {
      await testAgentMutation.mutateAsync({
        id,
        message: testMessage
      });
    } catch (err) {
      setTestError(err.message);
    } finally {
      setTestLoading(false);
    }
  };

  const renderDeploymentPanel = () => (
    <TabPanel>
      <Card className="p-6 space-y-6">
        {formData.channel === 'WEB' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Web Chat Configuration
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="allowAttachments"
                  checked={deploymentConfig.webConfig.allowAttachments}
                  onChange={(e) => handleDeploymentConfigChange('webConfig', 'allowAttachments', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <label htmlFor="allowAttachments" className="text-sm text-gray-700">
                  Allow file attachments
                </label>
              </div>

              {deploymentConfig.webConfig.allowAttachments && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max attachment size (MB)
                  </label>
                  <input
                    type="number"
                    value={deploymentConfig.webConfig.maxAttachmentSize}
                    onChange={(e) => handleDeploymentConfigChange('webConfig', 'maxAttachmentSize', Number(e.target.value))}
                    className="w-32 p-2 border border-gray-300 rounded-md"
                    min="1"
                    max="50"
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enableAudioMessages"
                  checked={deploymentConfig.webConfig.enableAudioMessages}
                  onChange={(e) => handleDeploymentConfigChange('webConfig', 'enableAudioMessages', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <label htmlFor="enableAudioMessages" className="text-sm text-gray-700">
                  Enable audio messages
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Widget Position
                </label>
                <select
                  value={deploymentConfig.webConfig.position}
                  onChange={(e) => handleDeploymentConfigChange('webConfig', 'position', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="top-left">Top Left</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Theme
                </label>
                <select
                  value={deploymentConfig.webConfig.theme}
                  onChange={(e) => handleDeploymentConfigChange('webConfig', 'theme', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {formData.channel === 'KIOSK' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Kiosk Configuration
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="fullScreen"
                  checked={deploymentConfig.kioskConfig.fullScreen}
                  onChange={(e) => handleDeploymentConfigChange('kioskConfig', 'fullScreen', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <label htmlFor="fullScreen" className="text-sm text-gray-700">
                  Full screen mode
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Idle Timeout (seconds)
                </label>
                <input
                  type="number"
                  value={deploymentConfig.kioskConfig.idleTimeout}
                  onChange={(e) => handleDeploymentConfigChange('kioskConfig', 'idleTimeout', Number(e.target.value))}
                  className="w-32 p-2 border border-gray-300 rounded-md"
                  min="60"
                  max="3600"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requireAuthentication"
                  checked={deploymentConfig.kioskConfig.requireAuthentication}
                  onChange={(e) => handleDeploymentConfigChange('kioskConfig', 'requireAuthentication', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <label htmlFor="requireAuthentication" className="text-sm text-gray-700">
                  Require user authentication
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="printerEnabled"
                  checked={deploymentConfig.kioskConfig.printerEnabled}
                  onChange={(e) => handleDeploymentConfigChange('kioskConfig', 'printerEnabled', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <label htmlFor="printerEnabled" className="text-sm text-gray-700">
                  Enable printer
                </label>
              </div>
            </div>
          </div>
        )}

        {formData.channel === 'VOICE' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Voice Configuration
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  value={deploymentConfig.voiceConfig.language}
                  onChange={(e) => handleDeploymentConfigChange('voiceConfig', 'language', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish</option>
                  <option value="fr-FR">French</option>
                  <option value="de-DE">German</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Voice
                </label>
                <select
                  value={deploymentConfig.voiceConfig.voice}
                  onChange={(e) => handleDeploymentConfigChange('voiceConfig', 'voice', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="neural-1">Neural Voice 1</option>
                  <option value="neural-2">Neural Voice 2</option>
                  <option value="standard-1">Standard Voice 1</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Speech Speed
                </label>
                <input
                  type="range"
                  value={deploymentConfig.voiceConfig.speed}
                  onChange={(e) => handleDeploymentConfigChange('voiceConfig', 'speed', Number(e.target.value))}
                  min="0.5"
                  max="2"
                  step="0.1"
                  className="w-full"
                />
                <div className="text-sm text-gray-500 mt-1">
                  {deploymentConfig.voiceConfig.speed}x
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pitch
                </label>
                <input
                  type="range"
                  value={deploymentConfig.voiceConfig.pitch}
                  onChange={(e) => handleDeploymentConfigChange('voiceConfig', 'pitch', Number(e.target.value))}
                  min="0.5"
                  max="2"
                  step="0.1"
                  className="w-full"
                />
                <div className="text-sm text-gray-500 mt-1">
                  {deploymentConfig.voiceConfig.pitch}x
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Embed Code
            </h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const embedCode = formData.embedCode || `<!-- Replace with actual embed code -->\n<script src="https://api.example.com/agent/${id}"></script>`;
                navigator.clipboard.writeText(embedCode);
              }}
            >
              Copy Code
            </Button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Use this code to embed the agent in your website or application.
          </p>
          <pre className="p-4 bg-gray-50 rounded-lg overflow-x-auto">
            <code className="text-sm">
              {formData.embedCode || `<!-- Replace with actual embed code -->\n<script src="https://api.example.com/agent/${id}"></script>`}
            </code>
          </pre>
        </div>
      </Card>
    </TabPanel>
  );

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        Error loading agent: {error.message}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="secondary"
          icon={ArrowLeft}
          onClick={handleNavigateAway}
        >
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id === 'new' ? 'Create Agent' : 'Edit Agent'}
          </h1>
          <p className="text-gray-500 mt-1">
            Configure how this agent interacts with users and handles tasks.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <TabGroup>
          <TabList className="mb-6">
            <Tab>Basic Info</Tab>
            <Tab>Greeting & Responses</Tab>
            <Tab>Policies & Skills</Tab>
            <Tab>Test Panel</Tab>
            <Tab>Deployment</Tab>
          </TabList>

          <TabPanels>
            {/* Basic Info Panel */}
            <TabPanel>
              <Card className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className={`w-full p-2 border rounded-md ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Channel
                  </label>
                  <select
                    value={formData.channel}
                    onChange={(e) => handleFormChange('channel', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="WEB">Web Chat</option>
                    <option value="KIOSK">In-store Kiosk</option>
                    <option value="VOICE">Voice</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              </Card>
            </TabPanel>

            {/* Greeting & Responses Panel */}
            <TabPanel>
              <Card className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Greeting Message
                  </label>
                  <textarea
                    value={formData.greeting}
                    onChange={(e) => handleFormChange('greeting', e.target.value)}
                    className={`w-full p-2 border rounded-md ${
                      formErrors.greeting ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows={3}
                    placeholder="Hi, I'm the Brand Re-Commerce Agent. How can I help?"
                  />
                  {formErrors.greeting && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.greeting}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fallback Message
                  </label>
                  <textarea
                    value={formData.fallbackMessage}
                    onChange={(e) => handleFormChange('fallbackMessage', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    rows={3}
                    placeholder="I'm not certain about that. Let me check with a human agent."
                  />
                </div>
              </Card>
            </TabPanel>

            {/* Policies & Skills Panel */}
            <TabPanel>
              <Card className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Policy Set
                  </label>
                  <select
                    value={formData.policySetId}
                    onChange={(e) => handleFormChange('policySetId', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">All Active Policies</option>
                    {policiesData?.policies.map(policy => (
                      <option key={policy.id} value={policy.id}>
                        {policy.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Skills
                  </label>
                  <div className="space-y-2">
                    {skillsData?.skills.map(skill => (
                      <label key={skill.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.allowedSkills.includes(skill.id)}
                          onChange={(e) => {
                            const newSkills = e.target.checked
                              ? [...formData.allowedSkills, skill.id]
                              : formData.allowedSkills.filter(id => id !== skill.id);
                            handleFormChange('allowedSkills', newSkills);
                          }}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span>{skill.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </Card>
            </TabPanel>

            {/* Test Panel */}
            <TabPanel>
              <Card className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Message
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={testMessage}
                      onChange={(e) => {
                        setTestMessage(e.target.value);
                        setTestError(null);
                      }}
                      placeholder="Type a test message..."
                      className={`flex-1 p-2 border rounded-md ${
                        testError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={testLoading}
                    />
                    <Button
                      type="button"
                      icon={Play}
                      onClick={handleTestAgent}
                      loading={testLoading}
                      disabled={testLoading}
                    >
                      Test
                    </Button>
                  </div>
                  {testError && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {testError}
                    </p>
                  )}
                </div>

                {testHistory.length > 0 && (
                  <div className="mt-4 space-y-4">
                    {testHistory.map((message, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg ${
                          message.role === 'user' 
                            ? 'bg-blue-50 ml-8' 
                            : 'bg-gray-50 mr-8'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-500">
                            {message.role === 'user' ? 'You' : 'Agent'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{message.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabPanel>

            {renderDeploymentPanel()}
          </TabPanels>
        </TabGroup>

        <div className="mt-6 flex justify-end gap-4">
          {formErrors.submit && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {formErrors.submit}
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={handleNavigateAway}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </form>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogPanel>
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Unsaved Changes
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                variant="secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                color="red"
                onClick={() => {
                  setShowConfirmDialog(false);
                  navigate(-1);
                }}
              >
                Leave Anyway
              </Button>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  );
} 